import React, { useEffect, useState, useRef, CSSProperties } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { convertMsToTime } from '../../../utils/utility';
import {
  CLAIM_VIEW_DEFAULT_COLOR_RANGES,
  PRE_AUTH_VIEW_DEFAULT_COLOR_RANGES,
} from '../../constants/commonConstants';
import {
  ColorRange,
  ElapsedTimeComponentProps,
} from '../../interfaces/tpaInterfaces';
import '../../../shared/components/tags/inpatienList.less';

const ElapsedTimeComponent: React.FC<ElapsedTimeComponentProps> = ({
  targetMs,
}: any): JSX.Element => {
  const initialOffsetMs = useRef(targetMs ?? 0);
  const startTime = useRef(Date.now());
  const { isPreAuthView, elapsedTimeColorConfig } = useSelector(
    (state: RootState): any => state?.selectedInPatientDetail || {},
  );
  const defaultOptions = isPreAuthView
    ? PRE_AUTH_VIEW_DEFAULT_COLOR_RANGES
    : CLAIM_VIEW_DEFAULT_COLOR_RANGES;
  const [elapsed, setElapsed] = useState(
    convertMsToTime(initialOffsetMs.current),
  );
  const [elapsedColorConfig, setElapsedColorConfig] = useState(defaultOptions);
  const [elapsedMs, setElapsedMs] = useState(initialOffsetMs.current);

  useEffect((): void => {
    setElapsedColorConfig(elapsedTimeColorConfig);
  }, [elapsedTimeColorConfig]);

  useEffect((): any => {
    const interval = setInterval((): void => {
      const now = Date.now();
      const diff = now - startTime.current + initialOffsetMs.current;
      setElapsed(convertMsToTime(diff));
      setElapsedMs(diff);
    }, 1000);

    return (): void => clearInterval(interval);
  }, []);

  const dynamicColor = getColorForValue(elapsedMs, elapsedColorConfig);
  if (!targetMs || !dynamicColor) {
    return <></>;
  }

  return (
    <div
      className="elapsed-time-container"
      style={
        {
          '--elapsed-time-container-bg-color': dynamicColor,
        } as CSSProperties
      }
    >
      {renderTimeUnit(elapsed?.days, 'day')}
      <Separator />
      {renderTimeUnit(elapsed?.hours, 'hr')}
      <Separator />
      {renderTimeUnit(elapsed?.minutes, 'min')}
      <Separator />
      {renderTimeUnit(elapsed?.seconds, 'sec')}
    </div>
  );
};

const getColorForValue = (
  value: number,
  ranges: ColorRange[],
): string | null => {
  if (!Array.isArray(ranges)) {
    return null;
  }
  const match = ranges.find(
    (range: any): boolean => value >= range?.min && value <= range?.max,
  );
  return match?.color ?? null;
};

const renderTimeUnit = (value: number, label: string): JSX.Element => (
  <div className="elapsed-time-block">
    <span className="elapsedTimeTimer">
      {value?.toString().padStart(2, '0')}
    </span>
    <span className="elapsedTimeText">{label}</span>
  </div>
);

const Separator = (): JSX.Element => (
  <span className="elapsed-time-separator">:</span>
); // Renders ":" between blocks

export default ElapsedTimeComponent;
